import React from "react";

import { EmbeddedLoadingChart } from "@/components/ui/embedded-loading-chart";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { DashboardCardType } from "../../../../types/dashboardLayout";
import {
  StatisticsCardHeadline,
  STATS_FONT_FAMILY,
} from "../../Statistics/components/statisticsCardHeadline";
import {
  statisticsShadcnCardClassName,
  statisticsShadcnCardContentClassName,
} from "../../Statistics/components/statisticsShadcnCardShell";
import {
  dashboardChartCardClassName,
  dashboardChartCardContentClassName,
  dashboardChartCardHeaderClassName,
  dashboardMutedTextClassName,
} from "./dashboardChartCardShell";
import {
  DASHBOARD_WIDGET_LOADING_DESCRIPTION,
  getDashboardWidgetLoadingMessage,
  getDashboardWidgetTitle,
} from "./dashboardWidgetLoadingMessages";

export type DashboardWidgetLoadingVariant = "chart" | "tall" | "table";

const LOADING_MIN_HEIGHT: Record<DashboardWidgetLoadingVariant, number> = {
  chart: 300,
  tall: 380,
  table: 280,
};

export interface DashboardWidgetLoadingChartProps {
  /** Screen-reader label only — no visible caption on the animation. */
  ariaLabel?: string;
  /**
   * Optional fixed height for standalone plot slots (e.g. portfolio growth Box).
   * Prefer omitting this inside padded card content so bottom padding/gaps stay intact.
   */
  height?: number;
  /** Soft floor when the loader fills a flex parent (statistics loading cards). */
  minHeight?: number;
  className?: string;
}

/** Tessera tile loader for an in-card chart/table plot area (card chrome lives elsewhere). */
export function DashboardWidgetLoadingChart({
  ariaLabel = "Loading chart",
  height,
  minHeight,
  className,
}: DashboardWidgetLoadingChartProps): React.ReactElement {
  return (
    <div
      data-dashboard-widget-loading-chart=""
      className={cn(
        "relative w-full overflow-hidden rounded-md",
        height == null && "h-full min-h-0",
        className
      )}
      style={{
        ...(height != null ? { height, minHeight: height } : null),
        ...(height == null && minHeight != null ? { minHeight } : null),
      }}
      aria-busy="true"
      aria-label={ariaLabel}
      role="status"
    >
      <EmbeddedLoadingChart />
    </div>
  );
}

export interface DashboardWidgetLoadingCardProps {
  cardType?: DashboardCardType;
  title?: string;
  status?: string;
  description?: string;
  variant?: DashboardWidgetLoadingVariant;
  /** Statistics card (default) or dashboard split-row chart card chrome. */
  chrome?: "statistics" | "dashboard-chart";
  className?: string;
}

/** Full dashboard widget shell with border, title, and a descriptive loading animation. */
export function DashboardWidgetLoadingCard({
  cardType,
  title: titleProp,
  status: statusProp,
  description = DASHBOARD_WIDGET_LOADING_DESCRIPTION,
  variant = "chart",
  chrome = "statistics",
  className,
}: DashboardWidgetLoadingCardProps): React.ReactElement {
  const title = titleProp ?? (cardType ? getDashboardWidgetTitle(cardType) : "Loading widget");
  const ariaLabel =
    statusProp ?? (cardType ? getDashboardWidgetLoadingMessage(cardType) : "Loading widget data");
  const minHeight = LOADING_MIN_HEIGHT[variant];

  if (chrome === "dashboard-chart") {
    return (
      <Card
        data-statistics-shadcn-card
        data-dashboard-sidebar-surface-card
        data-dashboard-widget-loading=""
        className={cn(dashboardChartCardClassName, className)}
      >
        <CardHeader className={dashboardChartCardHeaderClassName}>
          <div
            className={cn(
              "text-sm font-medium uppercase font-[family-name:var(--font-primary)]",
              dashboardMutedTextClassName
            )}
          >
            {title}
          </div>
        </CardHeader>
        {/* Fill the padded plot area — a fixed 300px child would eat pb-4 and sit flush. */}
        <CardContent className={cn(dashboardChartCardContentClassName, "relative overflow-hidden")}>
          <DashboardWidgetLoadingChart ariaLabel={ariaLabel} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      data-statistics-shadcn-card
      data-dashboard-widget-loading=""
      className={cn(statisticsShadcnCardClassName, className)}
      style={{ fontFamily: STATS_FONT_FAMILY }}
    >
      <CardHeader className="flex shrink-0 flex-col gap-1 border-b border-border px-[22px] py-4 sm:flex-row sm:items-start">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <StatisticsCardHeadline>{title}</StatisticsCardHeadline>
          <CardDescription className="text-xs" style={{ fontFamily: STATS_FONT_FAMILY }}>
            {description}
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent
        className={cn(
          statisticsShadcnCardContentClassName,
          "relative flex min-h-0 flex-1 flex-col overflow-hidden pt-4"
        )}
      >
        <DashboardWidgetLoadingChart
          ariaLabel={ariaLabel}
          className="min-h-0 w-full flex-1"
          minHeight={minHeight}
        />
      </CardContent>
    </Card>
  );
}
